import React, { useCallback, useEffect, useState } from 'react';
import Heading from '@react/react-spectrum/Heading';
import Select from '@react/react-spectrum/Select';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@material-ui/core';

import './FlexDiscountTemplatesTab.css';
import { OdTemplate } from '../../../models/OdDiscount';
import { getOdDiscountTemplates } from '../../../providers/OdDiscountsAPI';
import { getMarketSegmentOptions } from '../../../utils/Functions/utils';

interface FlexDiscountTemplatesTabProps {
    readonly partnerId: string;
}

function FlexDiscountTemplatesTab({ partnerId }: FlexDiscountTemplatesTabProps) {
    const marketSegmentOptions = getMarketSegmentOptions();

    const [marketSegment, setMarketSegment] = useState(marketSegmentOptions[0]?.value || 'COM');
    const [templates, setTemplates] = useState<OdTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const fetchTemplates = useCallback(async (segment: string) => {
        setIsLoading(true);
        try {
            const response = await getOdDiscountTemplates(partnerId, segment);
            if (response.httpstatus === 200) {
                setTemplates(response.data || []);
                setErrorMessage('');
            } else {
                setTemplates([]);
                setErrorMessage(response.message || 'Failed to fetch flex discount templates.');
            }
        } catch (e) {
            const err = e as any;
            setTemplates([]);
            setErrorMessage(err?.message || 'Failed to fetch flex discount templates.');
        } finally {
            setIsLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (partnerId && marketSegment) {
            fetchTemplates(marketSegment);
        }
    }, [partnerId, marketSegment, fetchTemplates]);

    let content: JSX.Element;
    if (isLoading) {
        content = <CircularProgress size={20} className="flex-discount-templates-loading-spinner" />;
    } else if (errorMessage) {
        content = <div className="flex-discount-templates-error">{errorMessage}</div>;
    } else {
        content = (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className="flex-discount-templates-table-head">Template ID</TableCell>
                            <TableCell className="flex-discount-templates-table-head">Label</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map((template) => (
                            <TableRow key={template.id}>
                                <TableCell>{template.templateId}</TableCell>
                                <TableCell>{template.label}</TableCell>
                            </TableRow>
                        ))}
                        {templates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2}>No flex discount templates found for this market segment.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <div>
            <Heading variant="subtitle3">View available closed discount templates</Heading>
            <Select
                aria-label="Market Segment"
                value={marketSegment}
                options={marketSegmentOptions}
                onSelect={(value: string) => setMarketSegment(value)}
            />
            <br />
            <br />

            {content}
        </div>
    );
}

export default FlexDiscountTemplatesTab;
